import { Box, Grid, SxProps, Theme, Typography, useMediaQuery } from "@mui/material";
import { getRoundName } from "../common/RoundHelper";
import { Link } from 'react-router-dom';
import { PoolXNumberFormat } from "../common/CurrencyFormatter";
import { IsRightToLeftTheme, GetFlexNumber, IsOrangeLight, productNamesC, themeC } from "../theme/themehelper";
import { BackIcon, BackIconGreen, Button, ForwardArrowIcon, TicketIcon, TicketIconWhite } from "../reusable-components";
import { ChevronRight } from '../images/gogoicons';
import kungTicket from '../images/ticket_kung.png';
import { UnibetReceiptIcon } from '../images/unibeticons';
import { CouponShare } from "../models/couponshares";
import { useCancel } from "../rounds/usePurchase";
import { getTheGameMatchName, isTheGameRoundName } from "../roundview/components/thegame/thegamemanifest";
import useTheGameManifest from "../roundview/components/thegame/usethegamemanifest";

function CouponShareListItem({ isActive, couponShare, t, canCancel, onCancelled }: { isActive: boolean, couponShare: CouponShare, t: any, canCancel: boolean, onCancelled?: () => void }) {
    const { stake, createdAt, result, id, coupon, fee, agentFee } = couponShare;
    const { round } = coupon || {};
    const { openTo } = round || {};
    const { currency = '' } = stake || {};
    const createdAtDate = createdAt ? new Date(createdAt) : null;
    const openToDate = openTo ? new Date(openTo) : null;
    const isTheGame = isTheGameRoundName(round?.name);
    const { manifest: theGameManifest } = useTheGameManifest(isTheGame ? { id: coupon.roundId, name: round.name } : undefined);
    const theGameName = isTheGame ? (theGameManifest ? getTheGameMatchName(theGameManifest) : t('the_game')) : undefined;
    const name = theGameName ?? getRoundName(round, t);
    let locale = t('locale');
    let theme = window.theme;
    const cancel = useCancel();
    const isTerminal = window.isTerminal;    
    const isSmallScreen = useMediaQuery('(max-width: 620px)');

    const handleCancel = async (couponShareToCancel: CouponShare) => {
        try {
            await cancel(couponShareToCancel);
            if (onCancelled) onCancelled();
        } catch (err) {
            console.error('Cancel failed in UI', err);
        }
    }

    if(round === undefined || createdAtDate== null || openToDate == null){
        return <></>;
    }

    let statusLabel = '';
    if (couponShare.status === 'CANCELED') {
        statusLabel = t('coupon_share_status_cancelled');
    } else if (couponShare.status === 'CASHED_OUT') {
        statusLabel = t('cashed_out');
    }

    let isRightToLeft = IsRightToLeftTheme(window.theme);

    const nf = PoolXNumberFormat(locale, { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const shortDate = Intl.DateTimeFormat(locale, { dateStyle: 'short' });
    const shortTime = Intl.DateTimeFormat(locale, { timeStyle: 'short', hour12: false, });

    let totAmount = 0;

    if (stake != null) {
        totAmount += Number(stake.amount)
    }

    if (fee != null) {
        totAmount += Number(fee.amount)
    }

    if (agentFee != null) {
        totAmount += Number(agentFee.amount)
    }

    const roundInfo: SxProps<Theme> = {
        fontWeight: 'bold',
        fontSize: '14px',
        color: 'var(--text-black-dark-background)'
    }

    const couponStyle: SxProps<Theme> = {
        flexDirection: "var(--flex-direction)",
        ...(theme === themeC.Green && {
            '&:hover': { backgroundColor: '#F5FFFA' },
        }),
    }

    const textStyle: SxProps<Theme> = {
        fontSize: '14px'
    }

    if (theme === themeC.GoGo || theme === themeC.Wood || theme === themeC.Orange) {
        const isOrangeTheme = theme === themeC.Orange;
        const isOrangeLightTheme = IsOrangeLight(theme);
        const themeFont = isOrangeTheme ? "'Hind'" : (theme === themeC.GoGo ? "'Poppins'" : "'GT-America'");
        let goGoName: string;
        const roundName = round.name;
        if (theGameName) {
            goGoName = theGameName;
        } else if (roundName.indexOf('Survivor') > -1) {
            goGoName = t('survivor8') + ' ' + GetFlexNumber(roundName);
        } else if (roundName.indexOf('Max') > -1) {
            goGoName = productNamesC.Orange.Max + ' 13';
        } else if (roundName.indexOf('Mix') > -1) {
            goGoName = productNamesC.Orange.Mix + ' 12';
        } else if (roundName.indexOf('Hockey') > -1) {
            goGoName = productNamesC.Orange.Hockey + ' ' + GetFlexNumber(roundName);
        } else {
            goGoName = productNamesC.Orange.Flex + ' ' + GetFlexNumber(roundName);
        }
        const isSurvivor = roundName.indexOf('Survivor') > -1;
        const survivorBorderGradient = 'linear-gradient(134.7deg, #B79734 1.69%, #DDB95F 17.48%, #FDEDAC 28.15%, #DDB95F 36.05%, #B79734 47.66%, #575757 88.98%)';
        const isLive = isActive && openToDate && openToDate < new Date();
        const innerContent = (
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: isSmallScreen ? 'auto' : (isSurvivor && !isOrangeTheme ? '58px' : '60px'),
                background: isOrangeLightTheme
                    ? (isSurvivor ? 'linear-gradient(90deg, #FFF0E0 0%, #FFFFFF 100%)' : '#FFFFFF')
                    : isOrangeTheme
                        ? (isSurvivor ? 'linear-gradient(90deg, #241D15 0%, #1E1E1E 30%)' : '#1E1E1E')
                        : (isSurvivor
                            ? 'linear-gradient(90deg, #1E1E1E 55.77%, #575757 100%)'
                            : 'linear-gradient(90deg, #1E1E1E 30.53%, #1E1E1E 100%)'),
                border: isOrangeTheme
                    ? (isSurvivor ? '1px solid #FE8000' : isOrangeLightTheme ? '1px solid #DDDDDD' : '1px solid #575757')
                    : (isSurvivor ? 'none' : '1px solid #575757'),
                borderRadius: isSurvivor && !isOrangeTheme ? '7px' : '8px',
                boxShadow: isSurvivor && !isOrangeTheme ? 'none' : '0px 1px 13px -2px rgba(16, 24, 40, 0.08)',
                overflow: 'hidden',
                ...(isSmallScreen && {
                    paddingTop: '6px',
                    paddingBottom: '6px',
                })
            }}>
                {/* Ticket icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 8px', gap: '10px', flexShrink: 0 }}>
                    <Box sx={{ width: '27px', height: '27px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isOrangeTheme
                            ? TicketIconWhite(27, 27, (isOrangeLightTheme || isActive) ? '#FE8000' : '#B3B3B3')
                            : (isSurvivor ? TicketIconWhite(27, 27, '#DDB95F') : TicketIcon)}
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: theGameName && !isSmallScreen ? 'nowrap' : 'wrap',
                    alignItems: 'center',
                    padding: '8px 0px 8px 8px',
                    flexGrow: 1,
                    minWidth: theGameName ? 0 : undefined,
                    rowGap: '6px',
                }}>
                    {/* Left: round name + date/time */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1px',
                        ...(theGameName
                            ? { flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }
                            : { flexShrink: 0, width: isOrangeTheme && !isSmallScreen ? '120px' : undefined }),
                    }}>
                        <Typography
                            noWrap={!!theGameName}
                            title={theGameName}
                            sx={{ fontFamily: themeFont, fontSize: '14px', fontWeight: 400, color: isOrangeLightTheme ? '#1F1F1F' : '#F0F0F0', lineHeight: '20px' }}
                        >
                            {goGoName}
                        </Typography>
                        <Typography sx={{ fontFamily: themeFont, fontSize: '14px', fontWeight: 400, color: isOrangeLightTheme ? '#636363' : isOrangeTheme ? '#B3B3B3' : '#F0F0F0', lineHeight: '20px' }}>
                            {shortDate.format(openToDate)}, {shortTime.format(openToDate)}
                        </Typography>
                    </Box>

                    {/* LIVE badge / CASHED OUT badge */}
                    {((isLive && isOrangeTheme) || couponShare.status === 'CASHED_OUT') && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 'auto',
                            minWidth: isSmallScreen ? '0px' : '50px',
                            flexShrink: 0,
                            marginLeft: isSmallScreen ? 'auto' : '8px',
                            paddingRight: isSmallScreen ? '8px' : '0px',
                        }}>
                            {isLive && isOrangeTheme && couponShare.status !== 'CASHED_OUT' && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #ED2024',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                }}>
                                    <Typography sx={{ fontFamily: themeFont, fontSize: '12px', fontWeight: 400, color: '#ED2024', lineHeight: '16px' }}>
                                        LIVE
                                    </Typography>
                                </Box>
                            )}
                            {couponShare.status === 'CASHED_OUT' && (() => {
                                let badgeColor = '#DDB95F';
                                if (theme === themeC.Orange) {
                                    badgeColor = '#FE8000';
                                } else if (theme === themeC.GoGo) {
                                    badgeColor = '#F14AA7';
                                } else if (theme === themeC.Wood) {
                                    badgeColor = '#B19661';
                                }
                                return (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: `1px solid ${badgeColor}`,
                                        borderRadius: '4px',
                                        padding: '2px 8px',
                                    }}>
                                        <Typography sx={{ fontFamily: themeFont, fontSize: '12px', fontWeight: 400, color: badgeColor, lineHeight: '16px' }}>
                                            {t('cashed_out')}
                                        </Typography>
                                    </Box>
                                );
                            })()}
                        </Box>
                    )}

                    {/* Right side info: Profit (to the left) + Stake (farthest right to stay aligned) */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginLeft: isSmallScreen ? '0px' : 'auto',
                        width: isSmallScreen ? '100%' : 'auto',
                        justifyContent: isSmallScreen ? 'space-between' : 'flex-end',
                        flexShrink: 0,
                        ...(isSmallScreen && {
                            borderTop: isOrangeTheme ? '1px dashed #FE8000' : '1px dashed #575757',
                            paddingTop: '6px',
                            marginTop: '2px',
                            paddingRight: '8px',
                        })
                    }}>
                        {/* Profit: only display if there is any */}
                        {result && result.amount > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOrangeTheme ? 'flex-end' : 'flex-start', padding: isSmallScreen ? '0px' : '0px 8px', gap: '1px', flexShrink: 0 }}>
                                <Typography sx={{ fontFamily: themeFont, fontSize: '14px', fontWeight: 400, color: isOrangeLightTheme ? '#636363' : '#B3B3B3', lineHeight: '20px' }}>
                                    {t('profit')}:
                                </Typography>
                                <Typography sx={{ fontFamily: themeFont, fontSize: '14px', fontWeight: 400, color: isOrangeLightTheme ? '#1F1F1F' : isOrangeTheme ? '#F0F0F0' : '#B3B3B3', lineHeight: '20px' }}>
                                    {nf.format(result.amount)}
                                </Typography>
                            </Box>
                        )}

                        {/* Stake label + amount */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOrangeTheme ? 'flex-end' : 'flex-start', padding: isSmallScreen ? '0px' : '0px 8px', gap: '1px', flexShrink: 0 }}>
                            <Typography sx={{ fontFamily: themeFont, fontSize: '14px', fontWeight: 400, color: isOrangeLightTheme ? '#636363' : '#B3B3B3', lineHeight: '20px' }}>
                                {t('stake')}:
                            </Typography>
                            <Typography sx={{ fontFamily: themeFont, fontSize: '14px', fontWeight: 400, color: isOrangeLightTheme ? '#1F1F1F' : isOrangeTheme ? '#F0F0F0' : '#B3B3B3', lineHeight: '20px' }}>
                                {nf.format(totAmount)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Arrow */}
                <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 8px', flexShrink: 0 }}>
                    {isOrangeTheme
                        ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke={isOrangeLightTheme ? '#1F1F1F' : '#F0F0F0'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        : <ChevronRight />}
                </Box>
            </Box>
        );

        const gogoContent = isSurvivor ? (
            <Box sx={{
                background: isOrangeTheme ? undefined : survivorBorderGradient,
                borderRadius: '8px',
                padding: isOrangeTheme ? '0px' : '1px',
                boxShadow: isOrangeTheme ? undefined : '0px 1px 13px -2px rgba(16, 24, 40, 0.08)',
                marginTop: '8px',
            }}>
                {innerContent}
            </Box>
        ) : (
            <Box sx={{ marginTop: '8px' }}>
                {innerContent}
            </Box>
        );

        if (isTerminal) {
            return gogoContent;
        }

        return (
            <Link to={`/coupon/${id}`} style={{ textDecoration: 'none' }}>
                {gogoContent}
            </Link>
        );
    }

    if (theme === themeC.Lion) {
        let lionName: string;
        const roundName = round.name;
        const isSurvivor = roundName.indexOf('Survivor') > -1;
        if (isSurvivor) {
            lionName = t('survivor8') + ' ' + GetFlexNumber(roundName);
        } else if (roundName.indexOf('Max') > -1) {
            lionName = productNamesC.Orange.Max + ' 13';
        } else if (roundName.indexOf('Mix') > -1) {
            lionName = productNamesC.Orange.Mix + ' 12';
        } else if (roundName.indexOf('Hockey') > -1) {
            lionName = productNamesC.Orange.Hockey + ' ' + GetFlexNumber(roundName);
        } else {
            lionName = productNamesC.Orange.Flex + ' ' + GetFlexNumber(roundName);
        }

        let isLive = false;
        const now = new Date().getTime();
        const roundOpenTo = Date.parse(round.openTo.toString());
        if (isActive && now > roundOpenTo) {
            isLive = true;
        }

        const lionContent = (
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: isSmallScreen ? 'auto' : '60px',
                background: isSurvivor ? 'linear-gradient(90deg, #FFF3F1 0%, #FFFFFF 100%)' : '#FFFFFF',
                border: isSurvivor ? '1px solid #FD5C3C' : '1px solid #DDDDDD',
                borderRadius: '8px',
                boxShadow: '0px 1px 13px -2px rgba(16, 24, 40, 0.08)',
                overflow: 'hidden',
                marginTop: '8px',
                ...(isSmallScreen && {
                    paddingTop: '6px',
                    paddingBottom: '6px',
                })
            }}>
                {/* Ticket icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 8px', gap: '10px', flexShrink: 0 }}>
                    <Box sx={{ width: '27px', height: '27px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {TicketIconWhite(27, 27, '#FD5C3C')}
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 0px 8px 8px',
                    flexGrow: 1,
                    rowGap: '6px',
                }}>
                    {/* Left: round name + date/time */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: isSmallScreen ? 'none' : 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: "'Roboto'", fontSize: '14px', fontWeight: 400, color: '#1F1F1F', lineHeight: '20px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {lionName}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Roboto'", fontSize: '14px', fontWeight: 400, color: '#636363', lineHeight: '20px', whiteSpace: 'nowrap' }}>
                            {shortDate.format(openToDate)}, {shortTime.format(openToDate)}
                        </Typography>
                    </Box>

                    {/* Live / Cashed Out badge */}
                    {((isLive && couponShare.status !== 'CASHED_OUT') || couponShare.status === 'CASHED_OUT') && (
                        <Box sx={{
                            width: 'auto',
                            minWidth: isSmallScreen ? '0px' : '44px',
                            display: 'flex',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginLeft: isSmallScreen ? 'auto' : '8px',
                            paddingRight: isSmallScreen ? '8px' : '0px',
                        }}>
                            {isLive && couponShare.status !== 'CASHED_OUT' && (
                                <Box sx={{
                                    border: '1px solid #E60000',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    fontFamily: "'Roboto'",
                                    color: '#E60000',
                                    whiteSpace: 'nowrap',
                                    lineHeight: '16px',
                                }}>
                                    {t('live')}
                                </Box>
                            )}
                            {couponShare.status === 'CASHED_OUT' && (
                                <Box sx={{
                                    border: '1px solid #FD5C3C',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                    fontFamily: "'Roboto'",
                                    color: '#FD5C3C',
                                    whiteSpace: 'nowrap',
                                    lineHeight: '16px',
                                }}>
                                    {t('cashed_out')}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Right side info: Profit (to the left) + Stake (farthest right to stay aligned) */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                        flexShrink: 0,
                        width: isSmallScreen ? '100%' : 'auto',
                        justifyContent: isSmallScreen ? 'space-between' : 'flex-end',
                        marginLeft: isSmallScreen ? 'auto' : '0px',
                        ...(isSmallScreen && {
                            borderTop: '1px dashed #DDDDDD',
                            paddingTop: '6px',
                            marginTop: '2px',
                            paddingRight: '8px',
                        })
                    }}>
                        {/* Profit: only display if there is any */}
                        {result && result.amount > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', flexShrink: 0, width: isSmallScreen ? 'auto' : '130px' }}>
                                <Typography sx={{ fontFamily: "'Roboto'", fontSize: '14px', fontWeight: 400, color: '#636363', lineHeight: '20px' }}>
                                    {t('profit')}:
                                </Typography>
                                <Typography sx={{ fontFamily: "'Roboto'", fontSize: '14px', fontWeight: 400, color: '#1F1F1F', lineHeight: '20px' }}>
                                    {nf.format(result.amount)}
                                </Typography>
                            </Box>
                        )}

                        {/* Stake */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', flexShrink: 0, width: isSmallScreen ? 'auto' : '130px' }}>
                            <Typography sx={{ fontFamily: "'Roboto'", fontSize: '14px', fontWeight: 400, color: '#636363', lineHeight: '20px' }}>
                                {t('stake')}:
                            </Typography>
                            <Typography sx={{ fontFamily: "'Roboto'", fontSize: '14px', fontWeight: 400, color: '#1F1F1F', lineHeight: '20px' }}>
                                {nf.format(totAmount)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Arrow */}
                <Box sx={{ display: 'flex', alignItems: 'center', padding: '0px 8px', flexShrink: 0 }}>
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
                        <path d="M1 1L6 6L1 11" stroke="#1F1F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </Box>
            </Box>
        );

        if (isTerminal) {
            return lionContent;
        }

        return (
            <Link to={`/coupon/${id}`} style={{ textDecoration: 'none' }}>
                {lionContent}
            </Link>
        );
    }

    let backGround = "var(--list-boxes-secondary)";
    if (!isActive) {
        backGround = "var(--list-boxes-primary)";
    }

    let betIcon = `url(${kungTicket})`;

    let icon = TicketIcon;
    icon = <Box sx={{ paddingTop: "10px" }} >
        {TicketIcon}
    </Box>

    if (theme === themeC.Kung) {
        icon = <Box sx={{ backgroundImage: betIcon, width: "17px", height: "17px" }} />
    }

    if (theme === themeC.Green) {
        icon = <UnibetReceiptIcon width={20} height={20} fill="#111111" />
    }


    if (theme === themeC.Hard) {
        textStyle.color = "var(--text-white)";
    }

    let content = <Grid container direction="row" wrap="nowrap" justifyContent="space-between" alignItems="center"
        sx={{
            backgroundColor: backGround,
            marginTop: "18px",
            padding: "15px",
            borderRadius: "var(--radius)",
            ...(theme === themeC.Reviant && {
                border: '0.666667px solid rgba(0, 0, 0, 0.1)',
            }),
        }} >
        <Grid container direction="column" flexGrow={0} width='30px' height="100%">
            {icon}
        </Grid>
        <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={couponStyle}>
            <Grid sx={{ minWidth: "100px" }}>
                <Grid container direction='column' alignItems='var(--flex-align)'
                    sx={{ marginLeft: "15px" }}>
                    <Typography sx={roundInfo}>{name}</Typography>
                    <Typography sx={roundInfo}>{shortDate.format(openToDate)}</Typography>
                    <Typography sx={roundInfo}>{shortTime.format(openToDate)}</Typography>
                    {couponShare.status === 'CASHED_OUT' && (() => {
                        let badgeColor = '#DDB95F';
                        if (theme === themeC.Dbet) badgeColor = '#112F7C';
                        else if (theme === themeC.Lyllo) badgeColor = '#F02884';
                        else if (theme === themeC.Green) badgeColor = '#147B45';
                        else if (theme === themeC.Hajper) badgeColor = '#A9F7CA';
                        else if (theme === themeC.Kryss) badgeColor = '#1191D7';
                        else if (theme === themeC.ComeOn) badgeColor = '#39F56A';
                        else if (theme === themeC.Snabbare) badgeColor = '#0168FF';
                        else if (theme === themeC.Stugan) badgeColor = '#7C0E19';
                        else if (theme === themeC.Hard) badgeColor = '#FACF0C';
                        else if (theme === themeC.Kung) badgeColor = '#004CFF';

                        return (
                            <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${badgeColor}`,
                                borderRadius: '4px',
                                padding: '2px 8px',
                                marginTop: '4px',
                                width: 'fit-content',
                            }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: badgeColor, lineHeight: '14px' }}>
                                    {t('cashed_out')}
                                </Typography>
                            </Box>
                        );
                    })()}
                </Grid>
            </Grid>

            {!isSmallScreen &&
                <Grid sx={{ minWidth: "100px" }}>
                    <Grid container direction='column' alignItems='var(--flex-align)'>
                        <Typography sx={textStyle}>{shortDate.format(createdAtDate)}</Typography>
                        <Typography sx={textStyle}>{shortTime.format(createdAtDate)}</Typography>
                    </Grid>
                </Grid>
            }

            <Grid sx={{ minWidth: "100px" }}>
                <Grid container direction='column' alignItems='var(--flex-align)'>
                    <Typography sx={textStyle}>{t('stake')}:</Typography>
                    <Typography sx={textStyle}>{nf.format(totAmount)}</Typography>
                </Grid>

                {result ? (
                    <Typography sx={textStyle}>{result !== null && result.amount > 0 ? nf.format(result.amount) : t('no_profit')}</Typography>
                ) : <Typography sx={textStyle}>{statusLabel}</Typography>}
            </Grid>
            <Box sx={{ paddingTop: "10px" }} >
                {canCancel && <Button onClick={() => handleCancel(couponShare)}>Cancel</Button>}

                {!canCancel && !isTerminal && (theme === themeC.GoGo
                    ? <ChevronRight />
                    : (!isRightToLeft ? (ForwardArrowIcon("var(--text-black-dark-background)")) : (theme === themeC.Green ? BackIconGreen() : BackIcon("var(--text-black-dark-background)"))))
                }
            </Box>
        </Grid>
    </Grid>

    if (isTerminal) {
        return content;
    }

    return (
        <Link to={`/coupon/${id}`} style={{ textDecoration: 'none' }}>
            {content}
        </Link >
    );
}

export default CouponShareListItem;
